import { EmployeeUserOption } from '~/hooks/apis/users/useGetEmployeeUsers';

import { FeedEntry, FeedMention } from '~/types/contracting/feed';

import { CHATTER_AUDIENCE_LABELS, CHATTER_MAX_BODY_LENGTH, CHATTER_MAX_MENTIONS } from './constants';
import { ChatterPost } from './types';

// An @mention is a run of capitalised words joined by spaces only, so it can't run onto the next line.
const MENTION_PATTERN = /@(LE Design Team|[A-Z][A-Za-z0-9.'-]*(?: +\([^)]+\))?(?: +[A-Z][A-Za-z0-9.'-]*)*)/g;
const HASHTAG_PATTERN = /(^|\s)#([A-Za-z][A-Za-z0-9/&+-]*)/g;

export const CHATTER_MENTION_CLASS = 'chatter-mention';
export const CHATTER_HASHTAG_CLASS = 'chatter-hashtag';

export function highlightMentionsAndHashtags(markdown: string) {
  return markdown
    .replace(HASHTAG_PATTERN, `$1<span class="${CHATTER_HASHTAG_CLASS}">#$2</span>`)
    .replace(MENTION_PATTERN, `<span class="${CHATTER_MENTION_CLASS}">@$1</span>`);
}

const ACTIVE_MENTION_PATTERN = /(?:^|\s)@([^@\n]{0,30})$/;

export function findActiveMention(text: string, caret: number) {
  const match = ACTIVE_MENTION_PATTERN.exec(text.slice(0, caret));
  if (!match) {
    return null;
  }
  return { query: match[1], start: caret - match[1].length - 1 };
}

export function toChatterPost(entry: FeedEntry): ChatterPost {
  return {
    id: entry.external_refs.salesforce,
    kind: entry.kind,
    // Only a group of updates by several people has no author, and its card doesn't show one.
    authorName: entry.author?.name ?? '',
    audience: CHATTER_AUDIENCE_LABELS[entry.audience],
    createdAt: entry.created_at,
    text: entry.body,
    changes: entry.changes.map((change) => ({
      field: change.field,
      oldValue: change.old_value,
      newValue: change.new_value,
    })),
    moreUpdateCount: entry.more_update_count,
    likeCount: entry.like_count,
    commentCount: entry.comment_count,
    comments: entry.comments.map((comment) => ({
      id: comment.external_refs.salesforce,
      authorName: comment.author.name,
      createdAt: comment.created_at,
      text: comment.body,
    })),
  };
}

export function addMention(mentions: Array<FeedMention>, employee: EmployeeUserOption) {
  return [...mentions, { email: employee.email, name: employee.full_name }];
}

// Drops people whose @mention was deleted from the text before sending.
export function mentionsInText(mentions: Array<FeedMention>, text: string) {
  const byEmail = new Map(
    mentions.filter((mention) => text.includes(`@${mention.name}`)).map((mention) => [mention.email, mention]),
  );
  return [...byEmail.values()];
}

/** Why a draft can't be sent yet, or undefined when it can. */
export function draftLimitMessage(text: string, mentions: Array<FeedMention>) {
  if (text.length > CHATTER_MAX_BODY_LENGTH) {
    return `Too long: ${text.length} of ${CHATTER_MAX_BODY_LENGTH} characters.`;
  }
  if (mentions.length > CHATTER_MAX_MENTIONS) {
    return `Too many mentions: ${mentions.length} of ${CHATTER_MAX_MENTIONS}.`;
  }
  return undefined;
}

export function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}
